import React, { useState } from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { CancelOrderModal } from '|/app/navigation-rail/components/modals';
import { useBrand } from '|/brand';
import Link from '|/components/carvana-ds/link';
import LinkButton from '|/components/carvana-ds/link-button';
import { OrderSummaryDisplay } from '|/components/carvana-ds/order-summary-display';
import CarvanaCareModal from '|/components/carvana-ds/order-summary-display/carvana-care-modal';
import GapCoverageModal from '|/components/carvana-ds/order-summary-display/gap-coverage-modal';
import VehicleImage from '|/components/vehicle-image';
import { getGapModalProps, getIsCarvanaFinanced } from '|/helpers/carvana-care-modal-variants';
import { getDealerFeeData, getDealerFeeAmount } from '|/helpers/dealer-fees';

import { useIsMobileViewport } from '|/hooks/use-is-mobile-viewport';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import styles from './order-summary.module.css';
import HowYourePayingSection from '../cio/components/how-youre-paying-section';
import TradeInDetailsModal from '../cio/components/trade-in-details-modal';
import TradeInSection from '../cio/components/trade-in-section';
import VehiclePriceSection from '../cio/components/vehicle-price-section';
import YourSelectionsSection from '../cio/components/your-selections-section';
import derivePriceDetails from '../cio/derive-price-details';

interface OrderSummaryProps {
  summary: ReviewSummaryResponse;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isCheckoutPage: boolean;
  /**
   * Unused here — the shared OrderSummary router spreads the same props into every arm and
   * the pre-check-it-out one needs it. This sheet's top tile has no "Edit order" affordance;
   * editing happens from the checkout page itself.
   */
  onEditOrder?: () => Promise<void>;
}

const OrderSummary = ({ summary, isOpen, setIsOpen, isCheckoutPage }: OrderSummaryProps) => {
  const [isTradeInModalOpen, setIsTradeInModalOpen] = useState(false);
  const [isCarvanaCareModalOpen, setIsCarvanaCareModalOpen] = useState(false);
  const [isGapModalOpen, setIsGapModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const { identity } = useBrand();
  const isMobileViewport = useIsMobileViewport();

  const gapModalProps = getGapModalProps(summary);
  const isFinanced = getIsCarvanaFinanced(summary);

  const { vehicle } = summary ?? {};
  const { year = null, make = null, model = null } = vehicle ?? {};

  const view = derivePriceDetails(summary, 'Slate');

  const userState = summary?.customer?.address?.state;
  const stateData = userState ? getDealerFeeData(userState) : null;
  const eventMetadata = stateData
    ? {
        feeBasedState: stateData.stateCode,
        maxDealerFeeDisplayed: getDealerFeeAmount(stateData)
      }
    : { feeBasedState: userState ?? null, maxDealerFeeDisplayed: null };

  // `drawerStyle` and `mobileStyle` are mutually exclusive in ds-modal's prop union
  // (drawer layout overrides mobile), so the two shapes are branched rather than merged.
  //
  // ds-modal 6.3 gives the sheet a slide-up animation and sizes it to its content. The sheet
  // has no height prop, so it is pinned to the bottom of the header via `classes.container`
  // — see `.sheetContainer`. The drag handle is hidden because the sheet is dismissed by its
  // close button, not by dragging.
  const modalStyleProps = isMobileViewport
    ? ({
        mobileStyle: 'sheet',
        showHandle: false,
        classes: { container: styles.sheetContainer }
      } as const)
    : ({ drawerStyle: 'all', drawerSide: 'right', drawerWidth: '400px' } as const);

  return (
    <>
      <EventedModal
        analyticsId={ModalNames.OrderSummary}
        aria-label="Price details"
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        {...modalStyleProps}
        eventMetadata={eventMetadata}
        contentPadding={false}
        disableOutsideClose={
          isTradeInModalOpen || isCarvanaCareModalOpen || isGapModalOpen || isCancelModalOpen
        }
        divLine={false}
      >
        <div className={styles.sheet} data-testid="order-summary-slate">
          <div className={styles.vehicleTile}>
            <div className={styles.vehicleRow}>
              <VehicleImage
                className={styles.vehicleImage}
                stockNumber={vehicle?.stockNumber}
                width={172}
                alt=""
                data-testid="vehicle-image"
              />
              <div className={styles.vehicleInfo}>
                <p className={styles.vehicleTitle}>
                  {[year, make, model].filter(Boolean).join(' ')}
                </p>
                <Link
                  href={identity.homeUrl}
                  variant="standalone"
                  size="sm"
                  data-analytics-id="order-summary-reconfigure-slate"
                  data-testid="pp_order-summary_reconfigure-slate"
                >
                  Reconfigure Your Slate
                </Link>
              </div>
            </div>
          </div>

          <h2 className={styles.sheetTitle}>Price details</h2>

          {/* Composed from the cio sections and modals rather than the older shared ones: the
              Slate Figma sheet is structurally the cio sheet, and mixing the two duplicates rows
              (both render a "Total purchase price"). The Slate-specific breakdown content —
              itemised accessories, accessories shipping, the "Nonrefundable vehicle
              shipping" wording, "Reconfigure Your Slate" — lives in those sections behind
              the brand capabilities, as does the suppression of the CarvanaCare row this design does
              not show. IncludedWithPurchase is omitted: this design has no such section. */}
          <div className={styles.breakdown}>
            <VehiclePriceSection summary={summary} view={view} />

            {view.showSelections && (
              <YourSelectionsSection
                summary={summary}
                view={view}
                onViewCarvanaCare={() => {
                  setIsCarvanaCareModalOpen(true);
                }}
                onViewGap={() => {
                  setIsGapModalOpen(true);
                }}
              />
            )}

            {view.showTradeIn && (
              <TradeInSection
                view={view}
                onViewTradeInDetails={() => {
                  setIsTradeInModalOpen(true);
                }}
              />
            )}
          </div>

          {view.showFinance && <HowYourePayingSection summary={summary} view={view} />}

          <OrderSummaryDisplay variant="sidebar">
            <TradeInDetailsModal
              summary={summary}
              isOpen={isTradeInModalOpen}
              onClose={() => {
                setIsTradeInModalOpen(false);
              }}
            />

            <CarvanaCareModal
              isOpen={isCarvanaCareModalOpen}
              setIsOpen={setIsCarvanaCareModalOpen}
              coverageType={
                (summary?.product?.type as 'Essential' | 'Cosmetic' | 'Plus' | 'Premier') ||
                'Essential'
              }
              coverageDetails={{
                duration: `${summary?.product?.months} months`,
                mileage: `+${summary?.product?.mileage?.toLocaleString()} miles`
              }}
              fuelDescription={summary?.vehicle?.fuelDescription}
            />

            <GapCoverageModal
              isOpen={isGapModalOpen}
              onClose={() => {
                setIsGapModalOpen(false);
              }}
              impactsFinancingTerms={gapModalProps.impactsFinancingTerms}
              vehicleModel={gapModalProps.vehicleModel}
              termMonths={gapModalProps.termMonths}
              monthlyDelta={gapModalProps.monthlyDelta}
              downDelta={gapModalProps.downDelta}
              termDelta={gapModalProps.termDelta}
              hasLoanComparison={gapModalProps.hasLoanComparison}
              fullPrice={summary?.gap?.price ?? 0}
              isFinanced={isFinanced}
              useSnapshotPricing
            />

            {isCheckoutPage && (
              <>
                <LinkButton
                  size="sm"
                  className={styles.cancelSection}
                  onClick={() => {
                    setIsCancelModalOpen(true);
                  }}
                  data-analytics-id="pp_order_summary_cancel_link"
                >
                  <span className={styles.cancelTextRegular}>
                    Want to <span className={styles.cancelTextLink}>cancel your order?</span>
                  </span>
                </LinkButton>
                <CancelOrderModal
                  isOpen={isCancelModalOpen}
                  onClose={() => {
                    setIsCancelModalOpen(false);
                  }}
                />
              </>
            )}
          </OrderSummaryDisplay>
        </div>
      </EventedModal>
    </>
  );
};

export default OrderSummary;
